import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { keyframes } from '@mui/material/styles';
import { SceneContainer } from './shared';

// --- Keyframe animations ---

const balloonFloat = keyframes`
  0% { transform: translateY(0); }
  50% { transform: translateY(-18px); }
  100% { transform: translateY(0); }
`;

const balloonFloat2 = keyframes`
  0% { transform: translateY(0) rotate(-3deg); }
  50% { transform: translateY(-14px) rotate(3deg); }
  100% { transform: translateY(0) rotate(-3deg); }
`;

const balloonFloat3 = keyframes`
  0% { transform: translateY(-5px) rotate(2deg); }
  50% { transform: translateY(-22px) rotate(-2deg); }
  100% { transform: translateY(-5px) rotate(2deg); }
`;

const kidWave = keyframes`
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(-12deg); }
  50% { transform: rotate(8deg); }
  75% { transform: rotate(-6deg); }
`;

const starTwinkle = keyframes`
  0%, 100% { opacity: 0.3; transform: scale(0.8); }
  50% { opacity: 1; transform: scale(1.2); }
`;

const confettiFall = keyframes`
  0% { transform: translateY(-10px) rotate(0deg); opacity: 0; }
  20% { opacity: 1; }
  100% { transform: translateY(80px) rotate(360deg); opacity: 0; }
`;

const textBounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
`;

const COLORS = {
  orange: '#FB923C',
  orangeText: '#C2410C', // WCAG AA compliant on white (~4.6:1)
  pink: '#F472B6',
  blue: '#60A5FA',
  green: '#34D399',
  yellow: '#FBBF24',
  purple: '#C084FC',
};

interface KidsLoadingAnimationProps {
  message?: string;
}

const KidsLoadingAnimationBase: React.FC<KidsLoadingAnimationProps> = ({
  message = 'Getting the fun ready...',
}) => {
  return (
    <SceneContainer>
      {/* Confetti particles */}
      {[
        { left: '15%', delay: '0s', color: COLORS.pink },
        { left: '30%', delay: '0.6s', color: COLORS.blue },
        { left: '50%', delay: '1.2s', color: COLORS.yellow },
        { left: '70%', delay: '0.3s', color: COLORS.green },
        { left: '85%', delay: '0.9s', color: COLORS.purple },
        { left: '22%', delay: '1.5s', color: COLORS.orange },
        { left: '65%', delay: '1.8s', color: COLORS.pink },
      ].map((p, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            top: 0,
            left: p.left,
            width: 8,
            height: 8,
            borderRadius: i % 2 === 0 ? '50%' : '2px',
            backgroundColor: p.color,
            animation: `${confettiFall} 2.5s ease-in-out ${p.delay} infinite`,
          }}
        />
      ))}

      {/* Twinkling stars */}
      {[
        { top: 15, left: '20%', delay: '0s' },
        { top: 25, left: '75%', delay: '0.5s' },
        { top: 40, left: '10%', delay: '1s' },
        { top: 10, left: '60%', delay: '1.5s' },
        { top: 50, left: '85%', delay: '0.8s' },
      ].map((s, i) => (
        <Box
          key={`star-${i}`}
          sx={{
            position: 'absolute',
            top: s.top,
            left: s.left,
            animation: `${starTwinkle} 2s ease-in-out ${s.delay} infinite`,
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill={COLORS.yellow}>
            <path d="M12 0L14.59 8.41L23 12L14.59 15.59L12 24L9.41 15.59L1 12L9.41 8.41Z" />
          </svg>
        </Box>
      ))}

      {/* Main scene SVG */}
      <svg
        width="260"
        height="200"
        viewBox="0 0 260 200"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        role="presentation"
        style={{ maxWidth: '100%', height: 'auto' }}
      >
        {/* Ground / grass */}
        <ellipse cx="130" cy="188" rx="120" ry="12" fill="#86EFAC" opacity={0.3} />

        {/* Balloon 1 - Orange (held by kid) */}
        <g style={{ animation: `${balloonFloat} 3s ease-in-out infinite` }}>
          <ellipse cx="115" cy="60" rx="20" ry="26" fill={COLORS.orange} />
          <ellipse cx="108" cy="50" rx="6" ry="8" fill="white" opacity={0.3} />
          <polygon points="115,86 112,90 118,90" fill={COLORS.orange} />
          <path d="M115 90 Q112 110 118 130 Q115 140 115 155" stroke={COLORS.orange} strokeWidth="1.5" fill="none" opacity={0.5} />
        </g>

        {/* Balloon 2 - Pink (free floating) */}
        <g style={{ animation: `${balloonFloat2} 3.5s ease-in-out 0.3s infinite` }}>
          <ellipse cx="155" cy="45" rx="17" ry="22" fill={COLORS.pink} />
          <ellipse cx="149" cy="37" rx="5" ry="7" fill="white" opacity={0.3} />
          <polygon points="155,67 152,71 158,71" fill={COLORS.pink} />
          <path d="M155 71 Q152 85 157 98" stroke={COLORS.pink} strokeWidth="1.2" fill="none" opacity={0.4} />
        </g>

        {/* Balloon 3 - Blue (free floating) */}
        <g style={{ animation: `${balloonFloat3} 4s ease-in-out 0.6s infinite` }}>
          <ellipse cx="85" cy="40" rx="15" ry="19" fill={COLORS.blue} />
          <ellipse cx="80" cy="33" rx="4" ry="6" fill="white" opacity={0.3} />
          <polygon points="85,59 82,63 88,63" fill={COLORS.blue} />
          <path d="M85 63 Q82 78 86 90" stroke={COLORS.blue} strokeWidth="1.2" fill="none" opacity={0.4} />
        </g>

        {/* Kid figure */}
        <g>
          {/* Body */}
          <rect x="105" y="158" width="20" height="22" rx="6" fill={COLORS.orange} />

          {/* Head */}
          <circle cx="115" cy="150" r="12" fill="#FDDCB5" />

          {/* Hair */}
          <path d="M103 148 Q105 138 115 136 Q125 138 127 148" fill="#8B5E3C" />

          {/* Eyes */}
          <circle cx="111" cy="150" r="1.5" fill="#333" />
          <circle cx="119" cy="150" r="1.5" fill="#333" />

          {/* Smile */}
          <path d="M111 154 Q115 158 119 154" stroke="#333" strokeWidth="1.2" fill="none" strokeLinecap="round" />

          {/* Waving arm */}
          <g style={{
            transformOrigin: '125px 160px',
            animation: `${kidWave} 2s ease-in-out infinite`,
          }}>
            <path d="M125 160 L138 148 L142 144" stroke="#FDDCB5" strokeWidth="5" fill="none" strokeLinecap="round" />
            {/* Hand */}
            <circle cx="142" cy="143" r="3.5" fill="#FDDCB5" />
          </g>

          {/* Legs */}
          <rect x="108" y="180" width="7" height="10" rx="3" fill="#60A5FA" />
          <rect x="117" y="180" width="7" height="10" rx="3" fill="#60A5FA" />

          {/* Shoes */}
          <rect x="106" y="188" width="10" height="4" rx="2" fill="#F472B6" />
          <rect x="116" y="188" width="10" height="4" rx="2" fill="#F472B6" />
        </g>
      </svg>

      {/* Loading text */}
      <Typography
        variant="body2"
        sx={{
          mt: 1,
          color: COLORS.orangeText,
          fontFamily: '"IBM Plex Sans", sans-serif',
          fontWeight: 600,
          letterSpacing: '0.04em',
          animation: `${textBounce} 2s ease-in-out infinite`,
        }}
      >
        {message}
      </Typography>
    </SceneContainer>
  );
};

export const KidsLoadingAnimation = React.memo(KidsLoadingAnimationBase);
